import { CheckCircleOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "antd";
import { useContext } from "react";
import NewSvg from "../../assets/image/new.svg?react";
import { HomeContext } from "../../containers/Home";
import { updateProjectItem } from "../../services/api-server/projects";
import { getUserStore } from "../../stores/userStore";
import { TProjectData } from "../../types/project";
import { alertMessage } from "../../utils/alert";
import Emitter from "../../utils/emitter";
import { checkRoles, includesIgnoreCase } from "../../utils/utils";

interface CloseProjectButtonProps {
	projectData: TProjectData;
}

function CloseProjectButton(props: CloseProjectButtonProps) {
	const { projectData } = props;
	const { currentTenantKey }: any = useContext(HomeContext);

	const queryClient = useQueryClient();

	const userStore = getUserStore();
	const currentUser = userStore.getCurrentUser();
	const current_user_email = currentUser?.email?.toLowerCase();
	const userRole = userStore.getUserRole();

	const updateMutation = useMutation({
		onMutate: () => {
			Emitter.emit("loading", { name: "close-project", isLoading: true });
		},
		mutationFn: async (updatedData: Partial<TProjectData>) => {
			const response = await updateProjectItem(
				currentTenantKey,
				projectData.project_code,
				updatedData,
				{ closed: projectData?.closed },
				currentUser,
				["closed"]
			);
			queryClient.invalidateQueries({
				queryKey: ["projects", currentTenantKey],
			});
			return response;
		},
		onSuccess: () => {
			Emitter.emit("loading", { name: "close-project", isLoading: false });
		},
		onError: () => {
			Emitter.emit("loading", { name: "close-project", isLoading: false });
		},
	});

	const handleCloseProject = async () => {
		try {
			const response = await updateMutation.mutateAsync({
				closed: true,
				status: projectData.progress === 100 ? "Resolved" : projectData.status,
			});
			if (response) {
				alertMessage("success", "Project has been closed successfully.");
			}
		} catch (error) {
			alertMessage("error", "Failed to close project. Please try again.");
		}
	};

	const handleReopenProject = async () => {
		try {
			const response = await updateMutation.mutateAsync({
				closed: false,
				status:
					projectData.status === "Resolved"
						? "In progress"
						: projectData.status,
			});
			if (response) {
				alertMessage("success", "Project has been reactivated successfully.");
			}
		} catch (error) {
			alertMessage("error", "Failed to reopen project. Please try again.");
		}
	};

	// if closed is false or undefined, then the project is open
	const projectIsOpen =
		projectData.closed !== true && projectData.status !== "Resolved";

	/**
     * Allow user with the following roles: 
        - Super admin
        - Tenant admin
        - Project manager
     */
	const isAllowed =
		checkRoles(userRole, ["tenant-admin"]) ||
		includesIgnoreCase(projectData?.project_manager, current_user_email);

	if (!isAllowed) {
		return null;
	}

	return projectIsOpen ? (
		<Button
			className="w-fit"
			icon={<CheckCircleOutlined />}
			type="primary"
			onClick={handleCloseProject}
		>
			Mark as resolved
		</Button>
	) : (
		<Button
			className="w-fit"
			icon={<NewSvg />}
			type="default"
			onClick={handleReopenProject}
		>
			Reactivate project
		</Button>
	);
}

export default CloseProjectButton;
